import { useEffect } from 'react';

import { useConsent } from 'hooks/useConsent';
import { useLocalization } from 'providers/LocalizationProvider';
import { googleTagManager } from 'services/scripts/googleTagManager';
import { hotjar } from 'services/scripts/hotjar';
import { injectScript } from 'services/scripts/injectScript';
import { survicateSurveyScript } from 'services/scripts/survicate';
import { isMonitoringAgentOrBot } from 'utils/browser';

import { useAmplitudeDeviceIdOverride } from './useAmplitudeDeviceIdOverride';

/**
 * Loads each tracker once its OneTrust consent category is granted, and reacts
 * to consent changes within the session. This is the consent-reactive injector
 * that "closes the gap" — every tracker is gated on the category it belongs to:
 *
 * - analytics  (C0002) → Amplitude, Hotjar
 * - functional (C0003) → Survicate
 * - marketing  (C0004) → Google Tag Manager + Facebook Pixel (fused)
 *
 * openSnowcat (also analytics) is gated separately in EventTrackingProvider via
 * the same `useConsent` source.
 */
export function useConsentedTrackers(): void {
  const { analytics, marketing, functional } = useConsent();
  const { language } = useLocalization();

  const enabled = !isMonitoringAgentOrBot();

  useAmplitudeDeviceIdOverride(enabled && analytics);

  useEffect(() => {
    if (!enabled || !analytics) return;

    injectScript(hotjar, 'hotjar-script');
  }, [enabled, analytics]);

  useEffect(() => {
    if (!enabled || !marketing) return;

    injectScript(googleTagManager, 'gtm-script');
  }, [enabled, marketing]);

  useEffect(() => {
    if (!enabled || !functional) return;

    injectScript(survicateSurveyScript(language), 'survicate-survey-snippet');
  }, [enabled, functional, language]);
}
